import { Add, Download, SystemUpdateAltOutlined } from '@mui/icons-material'
import { Button, Stack, Typography } from '@mui/material'
import type { FormikErrors } from 'formik'
import { parse, type ParseResult } from 'papaparse'
import { type ChangeEvent, type FC, useRef, useState } from 'react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import { useFormikProps } from './EditMultipleChoiceSet.hooks'
import ResponseSetTable from './ResponseSetTable'
import type { ResponseSetForm } from './ResponseSetTypes'

const sampleFileDownloadCSVData = [
  ['判別キー', 'カテゴリー', '選択肢名'],
  ['key1', 'category1', '選択肢1'],
  ['key2', 'category1,category2', '選択肢2'],
  ['key3', 'category2,category3', '選択肢3'],
]

const downloadSampleCSV = () => {
  // Windows の Excel が UTF-8 として開けるよう BOM を付ける
  const href = `data:text/csv;charset=utf-8,${encodeURIComponent(
    '\uFEFF' +
      sampleFileDownloadCSVData
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n'),
  )}`
  const link = document.createElement('a')
  link.href = href
  link.download = 'セット選択肢サンプル.csv'
  document.body.appendChild(link)
  try {
    link.click()
  } finally {
    document.body.removeChild(link)
  }
}
const csvRowLength = 3
// 最大行数を1000行に固定した。
// レコード数に上限を設けておかないとmobile側での表示にも影響が出ると考えられるため一定ラインの上限は必要
const csvRowMaxCount = 1000

const OptionsRow: FC = () => {
  const {
    formikProps: {
      setFieldValue,
      setFieldError,
      setFieldTouched,
      values,
      errors,
      touched,
    },
  } = useFormikProps()

  const inputEl = useRef<HTMLInputElement>(null)
  const [isLoadingCsvImport, setIsLoadingCsvImport] = useState(false)

  const parseResponsesFromFile = (e: ChangeEvent<HTMLInputElement>) => {
    setIsLoadingCsvImport(true)

    const file = e.target.files?.item(0)

    if (file) {
      parse(file, {
        complete: ({ data }: ParseResult<string[]>) => {
          if (data.length > csvRowMaxCount) {
            setFieldError(
              'responses',
              `csv取り込みの最大行数は${csvRowMaxCount}件までです`,
            )
            setFieldTouched('responses', true, false)
            setIsLoadingCsvImport(false)
            return
          }

          const responseSetForms: ResponseSetForm[] = data
            .slice(1)
            .filter(({ length }) => length === csvRowLength)
            .map((row, index) => ({
              key: index + 1,
              discriminationKey: row[0],
              categories: row[1]?.split(',').map((category, i) => ({
                name: category,
                key: `response${index}-category${i}`,
              })),
              name: row[2],
            }))

          setIsLoadingCsvImport(false)
          setFieldValue('responses', responseSetForms)
        },
      })
    }

    e.target.value = ''
  }

  const onDeleteResponseSet = (key: number) => {
    setFieldValue(
      'responses',
      values.responses?.filter(({ key: k }) => k !== key),
    )
  }

  const onChangeResponseSetDiscriminationKey = (
    discriminationKey: string,
    key: number,
  ) => {
    setFieldValue(
      'responses',
      values.responses?.map(response =>
        response.key !== key ? response : { ...response, discriminationKey },
      ),
    )
  }

  const onChangeResponseSetName = (name: string, key: number) => {
    setFieldValue(
      'responses',
      values.responses?.map(response =>
        response.key !== key ? response : { ...response, name },
      ),
    )
  }

  const onConfirmResponseSetCategoryTag = (
    responseKey: number,
    tagName: string,
  ) => {
    setFieldValue(
      'responses',
      values.responses?.map((response, i) =>
        response.key !== responseKey
          ? response
          : {
              ...response,
              categories: [
                ...(response.categories ?? []),
                {
                  name: tagName,
                  key: `response${i}-category${
                    (response.categories ?? []).length
                  }`,
                },
              ],
            },
      ),
    )
  }

  const onCloseResponseSetTag = (responseKey: number, tagIndex: number) => {
    setFieldValue(
      'responses',
      values.responses?.map(response =>
        response.key !== responseKey
          ? response
          : {
              ...response,
              categories: (response.categories ?? []).filter(
                (_, categoryTagIndex) => categoryTagIndex !== tagIndex,
              ),
            },
      ),
    )
  }

  const addResponse = () => {
    setFieldValue('responses', [
      ...values.responses,
      {
        name: '',
        discriminationKey: '',
        key: (values.responses?.slice(-1)[0]?.key ?? 0) + 1,
        categories: [],
      },
    ])
  }

  return (
    <Stack spacing={1} alignItems="flex-start">
      <Typography variant="subtitle1">選択肢</Typography>
      <Stack direction="row" spacing={1}>
        <Button
          color="secondary"
          variant="outlined"
          startIcon={<SystemUpdateAltOutlined />}
          onClick={() => inputEl?.current?.click()}
        >
          CSVインポート
        </Button>
        <input
          type="file"
          ref={inputEl}
          onChange={parseResponsesFromFile}
          accept=".csv"
          style={{ display: 'none' }}
        />
        <Button
          color="secondary"
          variant="outlined"
          startIcon={<Download />}
          onClick={downloadSampleCSV}
        >
          サンプルダウンロード
        </Button>
      </Stack>
      <ResponseSetTable
        isLoadingResponses={isLoadingCsvImport}
        responses={values.responses}
        onDelete={onDeleteResponseSet}
        onChangeDiscriminationKey={onChangeResponseSetDiscriminationKey}
        onChangeName={onChangeResponseSetName}
        onInputConfirmTag={onConfirmResponseSetCategoryTag}
        onCloseTag={onCloseResponseSetTag}
      />
      <Button variant="text" startIcon={<Add />} onClick={addResponse}>
        追加
      </Button>
      {typeof errors.responses === 'string' && (
        <FormikErrorMessage
          name="responses"
          errors={errors}
          touched={touched}
        />
      )}
      {Array.isArray(errors.responses) && touched.responses && (
        <ResponsesValidationErrors errors={errors.responses} />
      )}
    </Stack>
  )
}

const ResponsesValidationErrors: FC<{
  errors: (FormikErrors<ResponseSetForm> | string | undefined)[]
}> = ({ errors }) => {
  const messages: string[] = []
  errors.forEach((err, index) => {
    if (!err) return
    if (typeof err === 'string') {
      messages.push(`選択肢${index + 1}: ${err}`)
      return
    }
    if (err.name) messages.push(`選択肢${index + 1}: ${err.name}`)
    if (err.discriminationKey)
      messages.push(`選択肢${index + 1}: ${err.discriminationKey}`)
    if (Array.isArray(err.categories)) {
      err.categories.forEach((catErr, catIndex) => {
        if (!catErr) return
        if (typeof catErr === 'string') {
          messages.push(
            `選択肢${index + 1} カテゴリー${catIndex + 1}: ${catErr}`,
          )
          return
        }
        if (catErr.name)
          messages.push(
            `選択肢${index + 1} カテゴリー${catIndex + 1}: ${catErr.name}`,
          )
      })
    }
  })
  if (messages.length === 0) return null
  return (
    <Stack>
      {messages.map(msg => (
        <Typography key={msg} color="error">
          {msg}
        </Typography>
      ))}
    </Stack>
  )
}

export default OptionsRow
